import { computed } from "vue";
import { getSeasonName } from "@/composables/admin/dashboard/seasonUtils";

/**
 * Opciones de chart y datos derivados del dashboard principal.
 * Separado de useDashboard para mantener SRP: este composable solo
 * transforma datos de overview en formato consumible por Google Charts.
 *
 * @param {import('vue').Ref<Array>} seasonsRef — ref con la lista de temporadas
 * @param {import('vue').Ref<number[]>} selectedSeasonIdsRef — ref con los IDs seleccionados
 * @param {import('vue').Ref<object|null>} overviewDataRef — ref con los datos raw de overview
 * @param {import('vue').Ref<object>} loadedSeasonCacheRef — ref con caché de temporadas para comparativa
 * @returns {{
 *     revenueChartType: import('vue').ComputedRef<string>,
 *     occupancyChartType: import('vue').ComputedRef<string>,
 *     revenueChartData: import('vue').ComputedRef<Array>,
 *     occupancyChartData: import('vue').ComputedRef<Array>,
 *     comparisonTableData: import('vue').ComputedRef<Array>,
 * }}
 */
export function useDashboardCharts(
    seasonsRef,
    selectedSeasonIdsRef,
    overviewDataRef,
    loadedSeasonCacheRef,
    isMultiSeasonRef,
) {
    /** Entrada completa de la primera temporada seleccionada */
    const currentSeasonEntry = computed(() => {
        if (!overviewDataRef.value || !selectedSeasonIdsRef.value.length)
            return null;
        const firstId = selectedSeasonIdsRef.value[0];
        return overviewDataRef.value?.data?.per_season?.[firstId] || null;
    });

    /** Tipos de gráfico válidos de Google Charts */
    const validChartTypes = [
        "PieChart",
        "ColumnChart",
        "BarChart",
        "LineChart",
        "AreaChart",
    ];

    /** Tipo de gráfico de ingresos: PieChart (individual) o ColumnChart (comparativa) */
    const revenueChartType = computed(() => {
        const type = isMultiSeasonRef.value ? "ColumnChart" : "PieChart";
        return validChartTypes.includes(type) ? type : "ColumnChart";
    });

    /** Tipo de gráfico de ocupación: siempre ColumnChart */
    const occupancyChartType = computed(() => "ColumnChart");

    /** Datos para el gráfico de ingresos */
    const revenueChartData = computed(() => {
        if (!overviewDataRef.value) return [];
        const perSeason = overviewDataRef.value?.data?.per_season || {};

        if (isMultiSeasonRef.value) {
            // ColumnChart: ['Temporada', 'Abonos', 'Entradas']
            const rows = [["Temporada", "Abonos", "Entradas"]];
            for (const [seasonId, entry] of Object.entries(perSeason)) {
                rows.push([
                    getSeasonName(seasonsRef.value, seasonId),
                    entry.club?.abono_revenue || 0,
                    entry.club?.entrada_revenue || 0,
                ]);
            }
            return rows;
        }

        // Single season: PieChart
        const entry = currentSeasonEntry.value;
        if (!entry?.club) return [];
        return [
            ["Categoría", "Ingresos"],
            ["Abonos", entry.club.abono_revenue ?? 0],
            ["Entradas", entry.club.entrada_revenue ?? 0],
        ];
    });

    /** Datos para el gráfico de ocupación */
    const occupancyChartData = computed(() => {
        if (!overviewDataRef.value) return [];
        const perSeason = overviewDataRef.value?.data?.per_season || {};

        if (isMultiSeasonRef.value) {
            // ColumnChart: ['Temporada', 'Ocupación']
            const rows = [["Temporada", "Ocupación"]];
            for (const [seasonId, entry] of Object.entries(perSeason)) {
                rows.push([
                    getSeasonName(seasonsRef.value, seasonId),
                    Number(entry.avg_occupancy_pct || 0),
                ]);
            }
            return rows;
        }

        // Single season: ColumnChart con una sola barra
        if (!currentSeasonEntry.value) return [];
        return [
            ["Temporada", "Ocupación"],
            ["Actual", Number(currentSeasonEntry.value.avg_occupancy_pct ?? 0)],
        ];
    });

    /** Datos para la tabla comparativa de múltiples temporadas */
    const comparisonTableData = computed(() => {
        const cacheEntries = Object.entries(loadedSeasonCacheRef.value);
        if (cacheEntries.length === 0) return [];

        const rows = [];
        for (const [seasonId, seasonClub] of cacheEntries) {
            if (!seasonClub) continue;
            const seasonName = getSeasonName(seasonsRef.value, seasonId);
            rows.push({
                season: seasonName,
                _seasonId: Number(seasonId),
                revenue: seasonClub.total_revenue ?? 0,
                attendance: seasonClub.attendance_avg ?? 0,
                abonados: seasonClub.abonados_count ?? 0,
                occupancy: Number(seasonClub.avg_occupancy_pct ?? 0),
            });
        }

        // Ordenar: temporada más nueva primero (ID más alto = más nueva)
        rows.sort((a, b) => b._seasonId - a._seasonId);

        return rows;
    });

    return {
        revenueChartType,
        occupancyChartType,
        revenueChartData,
        occupancyChartData,
        comparisonTableData,
    };
}
